import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import {
    colors,
    lineGraphDotSize,
    lineGridStyle,
} from '../../../../shared/constants/constants';
import CustomTooltip from '../../CustomTooltip';
import {
    getXAxisLabel,
    getYAxisLabel,
    getBiaxialChartMargin,
    getYAxisTickFormatter,
    getXAxisTickFormatter,
} from '../../../dashboardUtils';

// eslint-disable-next-line complexity
function GaugeBiaxialBarWithLineGraph({
    barDataFormatter,
    barDataLabels,
    chartProperties,
    dataPoints,
    firstGroupKey,
    groupAxisFormatter,
    lineDataFormatter,
    lineDataLabels,
    legendFormatter,
    thresholds,
    xAxisLabel,
    yAxisLabelLeft,
    yAxisLabelRight,
}) {
    const [activeIndex, setActiveIndex] = useState(-1);
    const {
        barProperties,
        colorPalette = colors,
        composedChartProperties,
        leftSuffix = '',
        legendProperties,
        lineProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        rightSuffix = '',
        showLegend,
        showSinglePointTooltip,
        timeFormat,
        tooltipProperties,
        xAxisProperties,
        yAxisProperties,
    } = chartProperties;

    const xAxisTickFormatter = getXAxisTickFormatter(
        false,
        groupAxisFormatter,
        barDataFormatter,
        timeFormat,
        leftSuffix
    );

    const barAxisTickFormatter = getYAxisTickFormatter(
        false,
        groupAxisFormatter,
        barDataFormatter,
        timeFormat,
        leftSuffix
    );

    const lineAxisTickFormatter = getYAxisTickFormatter(
        false,
        groupAxisFormatter,
        lineDataFormatter,
        timeFormat,
        rightSuffix
    );

    const chartMargin = getBiaxialChartMargin(
        dataPoints,
        firstGroupKey,
        barDataLabels,
        lineDataLabels,
        barAxisTickFormatter,
        lineAxisTickFormatter
    );

    return (
        <ResponsiveContainer {...responsiveContainerProperties}>
            <ComposedChart {...composedChartProperties} data={dataPoints}>
                <CartesianGrid strokeDasharray={lineGridStyle} />
                <XAxis
                    {...xAxisProperties}
                    dataKey={firstGroupKey}
                    tickFormatter={xAxisTickFormatter}
                    label={getXAxisLabel(xAxisLabel, xAxisProperties)}
                />
                <YAxis
                    {...yAxisProperties}
                    yAxisId="left"
                    label={getYAxisLabel(yAxisLabelLeft)}
                    tickFormatter={barAxisTickFormatter}
                    width={chartMargin.left}
                />
                <YAxis
                    {...yAxisProperties}
                    yAxisId="right"
                    orientation="right"
                    label={getYAxisLabel(yAxisLabelRight, false)}
                    tickFormatter={lineAxisTickFormatter}
                    width={chartMargin.right}
                />
                <Tooltip
                    {...tooltipProperties}
                    cursor={dataPoints.length !== 0}
                    content={
                        <CustomTooltip
                            activeIndex={activeIndex}
                            axisFormatter={xAxisTickFormatter}
                            axisKey={firstGroupKey}
                            dataFormat={timeFormat}
                            dataFormatter={
                                barDataLabels[activeIndex]
                                    ? barDataFormatter
                                    : lineDataFormatter
                            }
                            legendFormatter={legendFormatter}
                            showSinglePoint={showSinglePointTooltip}
                            suffix={
                                barDataLabels[activeIndex]
                                    ? leftSuffix
                                    : rightSuffix
                            }
                            thresholds={thresholds}
                        />
                    }
                />
                {showLegend && (
                    <Legend {...legendProperties} formatter={legendFormatter} />
                )}
                {barDataLabels.map((barLabel, index) => (
                    <Bar
                        {...barProperties}
                        key={barLabel}
                        yAxisId="left"
                        dataKey={barLabel}
                        fill={colorPalette[index % colorPalette.length]}
                        onMouseOver={() => setActiveIndex(index)}
                        onMouseOut={() => setActiveIndex(-1)}
                        onFocus={() => setActiveIndex(index)}
                        onBlur={() => setActiveIndex(-1)}
                    />
                ))}
                {lineDataLabels.map((lineLabel, index) => (
                    <Line
                        {...lineProperties}
                        key={lineLabel}
                        yAxisId="right"
                        dataKey={lineLabel}
                        stroke={
                            colorPalette[
                                (barDataLabels.length + index) %
                                    colorPalette.length
                            ]
                        }
                        activeDot={{
                            r: lineGraphDotSize,
                            onMouseOver: () =>
                                setActiveIndex(barDataLabels.length + index),
                            onMouseOut: () => setActiveIndex(-1),
                            onFocus: () =>
                                setActiveIndex(barDataLabels.length + index),
                            onBlur: () => setActiveIndex(-1),
                        }}
                    />
                ))}
                {thresholds.map((threshold) => (
                    <ReferenceLine
                        key={`threshold-${threshold.id}`}
                        {...referenceLineProperties}
                        y={threshold.value}
                        stroke={threshold.lineColor}
                    />
                ))}
            </ComposedChart>
        </ResponsiveContainer>
    );
}

GaugeBiaxialBarWithLineGraph.propTypes = {
    barDataFormatter: PropTypes.func.isRequired,
    barDataLabels: PropTypes.instanceOf(Array),
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dataPoints: PropTypes.instanceOf(Array),
    firstGroupKey: PropTypes.string.isRequired,
    groupAxisFormatter: PropTypes.func.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    lineDataFormatter: PropTypes.func.isRequired,
    lineDataLabels: PropTypes.instanceOf(Array),
    thresholds: PropTypes.instanceOf(Array).isRequired,
    xAxisLabel: PropTypes.string,
    yAxisLabelLeft: PropTypes.string,
    yAxisLabelRight: PropTypes.string,
};

GaugeBiaxialBarWithLineGraph.defaultProps = {
    dataPoints: [],
    barDataLabels: [],
    lineDataLabels: [],
    xAxisLabel: null,
    yAxisLabelLeft: null,
    yAxisLabelRight: null,
};

export default React.memo(
    GaugeBiaxialBarWithLineGraph,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.thresholds, nextProps.thresholds)
);
